import { Badge } from '@/components/ui/badge';
import { Download } from 'lucide-react';
import { Link } from 'react-router';
import { formatDate, formatCurrencyNoDecimals } from '@/utils/formatters';
import { capitalCallNoticeFilename } from '../drawdown-notice-filename';
import { getDaysUntilDue } from '../transaction-status';
import { invoiceBreakdown } from '../invoice-breakdown';
import type { InvestorDrawdownSummary } from '../services/drawdowns-service';
import { useDocumentDownload } from '../../documents/hooks/useDocuments';
import React from 'react';

interface DrawdownListItemProps {
  drawdown: InvestorDrawdownSummary;
  variant?: 'urgent' | 'history';
}

function DueDateNote({
  isHistory,
  isOverdue,
  dueDate,
  lastPaymentDate,
}: {
  isHistory: boolean;
  isOverdue: boolean;
  dueDate: string;
  lastPaymentDate?: string;
}) {
  if (isHistory) {
    return (
      <span className="text-sm text-muted-foreground">
        completed{' '}
        {lastPaymentDate ? formatDate(lastPaymentDate) : formatDate(dueDate)}
      </span>
    );
  }
  if (isOverdue) {
    return (
      <span className="text-sm text-muted-foreground">
        was due {formatDate(dueDate)}
      </span>
    );
  }
  return (
    <span className="text-sm text-muted-foreground">
      due {formatDate(dueDate)}
    </span>
  );
}

export function DrawdownListItem({
  drawdown,
  variant = 'urgent',
}: DrawdownListItemProps) {
  const dueDate = drawdown.due_date;
  const daysUntilDue = getDaysUntilDue(dueDate);

  const amountDue = drawdown.investor_allocation.amount_due;
  const invoiceAmount = drawdown.investor_allocation.invoice_amount;
  const breakdown = invoiceBreakdown(drawdown.investor_allocation);
  const status = drawdown.investor_allocation.status;

  const isOverdue = status === 'overdue';
  const isHistory = variant === 'history';
  const openAccentClass = isHistory ? '' : 'border-l-4 border-l-amber-500';
  const { downloadDocument } = useDocumentDownload();

  const handleDownloadNotice = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!drawdown.notice_document_id) return;

    downloadDocument(
      drawdown.notice_document_id,
      capitalCallNoticeFilename(drawdown.purpose)
    );
  };

  return (
    <div
      className={`relative border border-border bg-card rounded-lg p-4 transition-all hover:shadow-md hover:border-muted-foreground/30 ${
        isOverdue ? 'border-l-4 border-l-red-500' : openAccentClass
      }`}
    >
      {/* Stretched link - the whole card navigates to the drawdown detail page */}
      <Link
        to={`/portal/transactions/drawdowns/${drawdown.id}`}
        aria-label={`View drawdown #${drawdown.drawdown_number} details`}
        className="absolute inset-0 rounded-lg"
      />

      {/* Header Section */}
      <div className="flex items-start justify-between gap-4 mb-3">
        {/* Left: Title, Drawdown Number, Purpose */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3
              className={`font-semibold ${isHistory ? 'text-muted-foreground' : 'text-foreground'}`}
            >
              {drawdown.fund_name}
            </h3>
            <span className="text-sm text-muted-foreground">
              Drawdown #{drawdown.drawdown_number}
            </span>
            {isOverdue && (
              <Badge variant="destructive" className="text-xs">
                {drawdown.investor_allocation.days_overdue}d overdue
              </Badge>
            )}
            {!isOverdue &&
              !isHistory &&
              daysUntilDue <= 7 &&
              daysUntilDue > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {daysUntilDue}d left
                </Badge>
              )}
          </div>
          {/* Purpose right below the title */}
          {drawdown.purpose && (
            <p className="text-sm text-muted-foreground">{drawdown.purpose}</p>
          )}
        </div>

        {/* Right: Actions (above the stretched link) */}
        {drawdown.notice_document_id && (
          <div className="relative z-10 flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 px-2"
              onClick={handleDownloadNotice}
            >
              <Download className="h-4 w-4" />
              <span>PDF</span>
            </button>
          </div>
        )}
      </div>

      {/* Content Row - Amount and Date info */}
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-xl font-bold text-foreground">
          {formatCurrencyNoDecimals(invoiceAmount)}
        </span>
        <DueDateNote
          isHistory={isHistory}
          isOverdue={isOverdue}
          dueDate={dueDate}
          lastPaymentDate={drawdown.investor_allocation.last_payment_date}
        />

        {/* Payment status inline */}
        {!isHistory && (
          <>
            <span className="text-sm text-border">•</span>
            {drawdown.investor_allocation.amount_paid > 0 ? (
              <>
                <span className="text-sm text-muted-foreground">
                  Still owed:{' '}
                  <span className="font-medium text-foreground">
                    {formatCurrencyNoDecimals(amountDue)}
                  </span>
                </span>
                <span className="text-sm text-border">•</span>
                <span className="text-sm text-muted-foreground">
                  Paid so far:{' '}
                  <span className="font-medium text-foreground">
                    {formatCurrencyNoDecimals(
                      drawdown.investor_allocation.amount_paid
                    )}
                  </span>
                </span>
                {drawdown.investor_allocation.payment_count &&
                  drawdown.investor_allocation.payment_count > 1 && (
                    <>
                      <span className="text-sm text-border">•</span>
                      <span className="text-sm text-muted-foreground">
                        {drawdown.investor_allocation.payment_count}{' '}
                        installments
                      </span>
                    </>
                  )}
              </>
            ) : (
              <span className="text-sm font-medium text-foreground">
                No payment received yet
              </span>
            )}
          </>
        )}

        {isHistory && (
          <>
            <span className="text-sm text-border">•</span>
            {drawdown.investor_allocation.payment_count &&
            drawdown.investor_allocation.payment_count > 1 ? (
              <span className="text-sm text-muted-foreground">
                Paid in{' '}
                <span className="font-medium text-foreground">
                  {drawdown.investor_allocation.payment_count} installments
                </span>
              </span>
            ) : (
              <span className="text-sm font-medium text-foreground">
                Paid in full
              </span>
            )}
          </>
        )}
      </div>

      {breakdown && (
        <p className="mt-1 text-sm text-muted-foreground">{breakdown}</p>
      )}
    </div>
  );
}
